import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Field, Formik } from "formik";
import * as Yup from "yup";
import ReactDOMServer from "react-dom/server";
import {
  AppRemixIcon,
  AppModal,
  AppSingleSelect,
  AppMultiSelect,
} from "@/layout/AppComponents";
import GenerateReport from "../../../layout/screenSpecifics/Modules/Intelligence/GenerateReport";
import GenerateProfileReport from "../../../layout/screenSpecifics/Modules/Intelligence/GenerateProfileReport";
import ReportOptionsSwitch from "../../../layout/screenSpecifics/Modules/Intelligence/ReportOptionsSwitch";
import Translator from "../../../layout/components/helpers/Translator";
import AppLink from "../../../routing/helpers/AppLink";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import SpinnerLoader from "../../../layout/components/helpers/SpinnerLoader";
import NoInfo from "../../../layout/components/helpers/NoInfo";
import colors from "../../../constants/colors";
import AppTextArea from "../../../layout/components/form/AppTextArea";
import SelectPlants from "../../../layout/components/form/Selects/SelectPlants";
import SelectPlantGroups from "../../../layout/components/form/Selects/SelectPlantGroups";
import ModulePermission from "../../../layout/components/helpers/ModulePermission";
import NotificationAlert from "../../../layout/components/helpers/notificationAlert";
import OnlineInfoSwitch from "../../../layout/screenSpecifics/Modules/Intelligence/OnlineInfoSwitch";
import { axiosPath } from "../../../routing/helpers/router";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";
import LoopModal from "../../../layout/components/helpers/LoopModal";

const IntelligenceHomeScreen = () => {
  const { moduleBridgeHex } = useParams();
  ModulePermission(moduleBridgeHex);
  const isMounted = useIsMounted();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSaveProfileModal, setShowSaveProfileModal] = useState(false);
  const [showLoadProfileModal, setShowLoadProfileModal] = useState(false);
  const [defaultReportValues, setDefaultReportValues] = useState({});
  const [valuesToProfile, setValuesToProfile] = useState(null);
  const [intelligenceProfiles, setIntelligenceProfiles] = useState([]);
  const [triggerStartReport, setTriggerStartReport] = useState();
  const [triggerStartProfileReport, setTriggerStartProfileReport] = useState();
  const [onlineInfo, setOnlineInfo] = useState(null);

  const [newReports] = useState([
    1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 23, 24, 25, 26, 27, 30, 52, 53, 54, 55, 56, 57,
    58, 59,
  ]);

  const [formatOptions] = useState([
    {
      value: "plants",
      label: ReactDOMServer.renderToString(
        <Translator file="user" name="user.plants" />,
      ),
    },
    {
      value: "plantGroups",
      label: ReactDOMServer.renderToString(
        <Translator file="user" name="module.plantGroups" />,
      ),
    },
  ]);

  // > LOAD MODULE
  const loadModule = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_intelligence_home"),
        { moduleBridgeHex },
      );
      return response.data;
    } catch (err) {
      // WIP
      // dispatch(errorHandler({message: 'Error', isGlobal: true}))
    }
  }, []);

  useEffect(() => {
    loadModule().then((response) => {
      if (isMounted.current) {
        setLoading(false);
        setModule(response);
        setIntelligenceProfiles(response.intelligenceProfiles);
      }
    });
  }, [moduleBridgeHex]);
  // < LOAD MODULE

  const handleReportChange = (value, resetForm, setFieldValue) => {
    resetForm();
    const sr = module.reports.find((r) => r.type.intReportId === value);
    setFieldValue("report", sr);
    setOnlineInfo(null);
  };

  const handleResetForm = (resetForm) => {
    resetForm();
    setOnlineInfo(null);
  };

  const handleReportTypeChange = (e, resetForm, setFieldValue, values) => {
    const { report } = values;
    resetForm();
    setFieldValue("report", report);
    setFieldValue("format", e);
    setOnlineInfo(null);
  };

  // > CRUD PROFILE
  const handleShowSaveProfileModal = (status, values) => {
    setValuesToProfile(values);
    setShowSaveProfileModal(status);
  };
  const handleSaveProfile = async (formName) => {
    setIntelligenceProfiles([]);
    try {
      const response = await axios.post(
        axiosPath("api_r_user_m_intelligence_crudProfile"),
        { form: formName, values: valuesToProfile, moduleBridgeHex },
      );
      if (response.data.error) {
        NotificationAlert({
          title: ReactDOMServer.renderToString(
            <Translator file="user" name="user.error" />,
          ),
          message: ReactDOMServer.renderToString(
            <Translator
              file="module-intelligence"
              name="existing.name.choose.another"
            />,
          ),
          type: "danger",
        });
      } else {
        setIntelligenceProfiles(response.data.intelligenceProfiles);
        setShowSaveProfileModal(false);
        NotificationAlert({
          title: ReactDOMServer.renderToString(
            <Translator file="module-intelligence" name="created" />,
          ),
          message: ReactDOMServer.renderToString(
            <Translator file="module-intelligence" name="profile.saved" />,
          ),
          type: "success",
        });
      }
    } catch (err) {}
  };
  const handleLoadProfile = async (values, { resetForm }) => {
    setIntelligenceProfiles([]);
    try {
      const response =
        values.mode === "delete"
          ? await axios.post(
              axiosPath("api_r_user_m_intelligence_crudProfile"),
              { form: values, moduleBridgeHex },
            )
          : values.mode === "load" &&
            (await axios.post(
              axiosPath("api_r_user_m_intelligence_crudProfile"),
              { form: values, moduleBridgeHex },
            ));
      setIntelligenceProfiles(response.data.intelligenceProfiles);
      resetForm();
      setShowLoadProfileModal(false);
      if (values.mode === "load") {
        const newDefaultReportValues = {};
        Object.keys(response.data.loadedProfile).forEach((key) => {
          newDefaultReportValues[key] = response.data.loadedProfile[key];
        });
        setDefaultReportValues(newDefaultReportValues);
      }
    } catch (err) {}
  };

  // < CRUD PROFILE

  // > GENERATE REPORT
  const handleOnGenerateReport = (values) => {
    setOnlineInfo(null);
    setTriggerStartReport(values);
  };
  const handleOnGenerateReportFinish = (response) => {
    setTriggerStartReport(null);
    if (typeof response === "string") window.open(response, "_self");
    else {
      if (response.reportUrl) window.open(response.reportUrl, "_self");
      setOnlineInfo(response);
    }
  };
  const handleOnGenerateReportLoopFinish = (response) => {
    setTriggerStartReport(null);
    if (typeof response === "string") window.open(response, "_self");
    else {
      if (response.reportUrl) window.open(response.reportUrl, "_self");
      setOnlineInfo(response);
    }
  };
  const handleOnGenerateReportError = () => {
    setTriggerStartReport(false);
    NotificationAlert({
      title: ReactDOMServer.renderToString(
        <Translator file="user" name="user.error" />,
      ),
      message: ReactDOMServer.renderToString(
        <Translator
          file="module-intelligence"
          name="error.report.not.created"
        />,
      ),
      type: "danger",
      position: "top-center",
      duration: 0,
    });
  };
  // < GENERATE REPORT

  // > GENERATE PROFILE REPORT
  const handleOnGenerateProfileReport = (values) => {
    setTriggerStartProfileReport(values);
  };
  const handleOnGenerateProfileReportFinish = () => {
    setTriggerStartProfileReport(null);
    NotificationAlert({
      title: ReactDOMServer.renderToString(
        <Translator file="module-intelligence" name="finished" />,
      ),
      message: ReactDOMServer.renderToString(
        <Translator
          file="module-intelligence"
          name="reports.saved.correctly"
        />,
      ),
      type: "success",
      position: "top-center",
      duration: 0,
    });
  };
  // > GENERATE PROFILE REPORT

  return (
    <div className="relative m_sr pl-2 pr-2">
      {module ? (
        <>
          <BreadcrumbHandler paths={[{ name: module.name }]} />
          {triggerStartReport && (
            <>
              {newReports.includes(triggerStartReport?.report?.type?.id) ? (
                <LoopModal
                  route="api_r_user_m_intelligence_calculateReport"
                  value={{
                    values: triggerStartReport,
                    moduleBridgeHex,
                  }}
                  onFinish={handleOnGenerateReportLoopFinish}
                  onError={handleOnGenerateReportError}
                />
              ) : (
                <GenerateReport
                  moduleBridgeHex={moduleBridgeHex}
                  values={triggerStartReport}
                  onFinish={handleOnGenerateReportFinish}
                  onError={handleOnGenerateReportError}
                />
              )}
            </>
          )}
          {triggerStartProfileReport && (
            <GenerateProfileReport
              moduleBridgeHex={moduleBridgeHex}
              values={triggerStartProfileReport}
              onFinish={handleOnGenerateProfileReportFinish}
            />
          )}

          <section className="content">
            <div className="row m-0">
              <div className="col-8">
                <Formik
                  initialValues={defaultReportValues}
                  onSubmit={handleOnGenerateReport}
                  validationSchema={() =>
                    Yup.lazy((values) => {
                      if (values.report?.type?.id === 44) {
                        return Yup.object().shape({
                          plants: Yup.array()
                            .min(1, "Mínimo 1 instalación")
                            .max(50, "Máximo 50 instalaciones"),
                          plantGroups: Yup.array()
                            .min(1, "Mínimo 1 grupo de instalaciones")
                            .max(10, "Máximo 10 grupos de instalaciones"),
                        });
                      }
                      return Yup.object();
                    })
                  }
                  enableReinitialize
                >
                  {({
                    setFieldValue,
                    handleSubmit,
                    values,
                    resetForm,
                    errors,
                  }) => (
                    <div className="card">
                      <div className="card-header">
                        <Translator
                          file="module-intelligence"
                          name="module.int.settings"
                        />
                        <small
                          className="float-right text-info pointer"
                          onClick={() => setShowLoadProfileModal(true)}
                        >
                          <Translator
                            file="module-intelligence"
                            name="profiles"
                          />{" "}
                          ({intelligenceProfiles.length})
                        </small>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-12">
                            <div className="row">
                              <div className="col-sm-12">
                                <AppSingleSelect
                                  icon={<AppRemixIcon icon="file-list" />}
                                  options={module.reports}
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-intelligence"
                                      name="module.int.report-type"
                                    />,
                                  )}
                                  placeholder={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-intelligence"
                                      name="module.int.select-report"
                                    />,
                                  )}
                                  value={values?.report?.type?.intReportId}
                                  hasSearchBox
                                  onChange={(opt) =>
                                    handleReportChange(
                                      opt,
                                      resetForm,
                                      setFieldValue,
                                    )
                                  }
                                />
                              </div>
                            </div>
                            {module.options.hidePlants != true &&
                              module.options.hidePlantGroups != true &&
                              values?.report?.type.settings.format.length >
                                1 && (
                                <div className="row">
                                  <div className="col-sm-12">
                                    <AppSingleSelect
                                      icon={
                                        <AppRemixIcon icon="layout-column" />
                                      }
                                      options={formatOptions}
                                      title={
                                        <Translator
                                          file="module-intelligence"
                                          name="format"
                                        />
                                      }
                                      placeholder={ReactDOMServer.renderToString(
                                        <Translator
                                          file="module-intelligence"
                                          name="select.an.option"
                                        />,
                                      )}
                                      value={values?.format}
                                      onChange={(e) =>
                                        handleReportTypeChange(
                                          e,
                                          resetForm,
                                          setFieldValue,
                                          values,
                                        )
                                      }
                                    />
                                  </div>
                                  {console.log('values', values)}
                                </div>
                              )}
                            {((values?.report?.type.settings.format.length ==
                              1 &&
                              values?.report?.type.settings.format.includes(
                                "plants",
                              )) ||
                              values?.format === "plants" ||
                              (values?.report?.type.settings.format.includes(
                                "plants",
                              ) &&
                                module.options.hidePlantGroups == true)) && (
                              <div className="row">
                                <div className="col-lg-12">
                                  <SelectPlants
                                    type="userPlants"
                                    format="m"
                                    isSubmodule={
                                      !!values?.report?.type.settings
                                        ?.isSubmodule
                                    }
                                    value={values.plants || []}
                                    error={errors?.plants}
                                    onHandleChange={(opt) =>
                                      setFieldValue("plants", opt)
                                    }
                                    isMulti
                                  />
                                </div>
                              </div>
                            )}
                            {((values?.report?.type.settings.format.length ==
                              1 &&
                              values?.report?.type.settings.format.includes(
                                "allPlants",
                              )) ||
                              values?.format === "allPlants" ||
                              (values?.report?.type.settings.format.includes(
                                "allPlants",
                              ) &&
                                module.options.hidePlantGroups == true)) && (
                              <div className="row">
                                <div className="col-lg-12">
                                  <SelectPlants
                                    type="userPlantsWithoutObligations"
                                    format="m"
                                    isSubmodule={
                                      !!values?.report?.type.settings
                                        ?.isSubmodule
                                    }
                                    value={values.plants}
                                    error={errors?.plants}
                                    onHandleChange={(opt) =>
                                      setFieldValue("plants", opt)
                                    }
                                    isMulti
                                  />
                                </div>
                              </div>
                            )}
                            {((values?.report?.type.settings.format.length ==
                              1 &&
                              values?.report?.type.settings.format.includes(
                                "plantGroups",
                              )) ||
                              values?.format === "plantGroups" ||
                              (values?.report?.type.settings.format.includes(
                                "plantGroups",
                              ) &&
                                module.options.hidePlants == true)) && (
                              <div className="row">
                                <div className="col-lg-12">
                                  <SelectPlantGroups
                                    type="all"
                                    onHandleChange={(opt) => {
                                      console.log('opt',opt)
                                      setFieldValue("plantGroups", opt);
                                    }}
                                    reset={!values.plantGroups}
                                    error={errors?.plantGroups}
                                  />
                                </div>
                              </div>
                            )}
                            {(values.plants ||
                              values.plantGroups ||
                              values?.report?.type.settings.format.length ==
                                0) &&
                              values?.report && (
                                <ReportOptionsSwitch
                                  reportType={values?.report?.type.id}
                                  handleOnSubmit={handleSubmit}
                                  handleOnShowSaveProfileModal={(r) =>
                                    handleShowSaveProfileModal(r, values)
                                  }
                                  handleOnSetFieldValue={setFieldValue}
                                  values={values}
                                  errors={errors}
                                  selectedReport={values?.report}
                                />
                              )}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12 text-right mt-2">
                            {!values?.report ? (
                              <span className="btn btn-outline-light btn-xs disabled">
                                <AppRemixIcon icon="arrow-go-back" />
                                <Translator
                                  file="module-intelligence"
                                  name="report.restart"
                                />
                              </span>
                            ) : (
                              <span
                                className="btn btn-outline-light btn-xs"
                                onClick={() => handleResetForm(resetForm)}
                              >
                                <AppRemixIcon icon="arrow-go-back" />
                                <Translator
                                  file="module-intelligence"
                                  name="report.restart"
                                />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Formik>
              </div>
              <div className="col-4">
                <div className="card">
                  <div className="card-header">
                    <Translator
                      file="module-intelligence"
                      name="store.reports"
                    />
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-12">
                        <Formik
                          initialValues={{
                            profiles: [],
                            comment: "",
                          }}
                          onSubmit={handleOnGenerateProfileReport}
                        >
                          {({ setFieldValue, handleSubmit, values }) => (
                            <>
                              <div className="row">
                                <div className="col-sm-12">
                                  {intelligenceProfiles.length > 0 ? (
                                    <AppMultiSelect
                                      options={intelligenceProfiles}
                                      onChange={(e) =>
                                        setFieldValue("profiles", e)
                                      }
                                      title={
                                        <Translator
                                          file="module-intelligence"
                                          name="profile"
                                        />
                                      }
                                      placeholder={ReactDOMServer.renderToString(
                                        <Translator
                                          file="module-intelligence"
                                          name="select.one.or.various.profiles"
                                        />,
                                      )}
                                      icon={
                                        <AppRemixIcon icon="inbox-unarchive" />
                                      }
                                      setValueField="id"
                                      setLabelField="name"
                                      value={values.profiles}
                                      isMulti
                                      hasSearchBox
                                      hasSelectOptions
                                    />
                                  ) : (
                                    <span className="form-control mb-3" />
                                  )}
                                </div>
                              </div>
                              {values.profiles &&
                                values.profiles.length > 0 && (
                                  <>
                                    <div className="row mb-3">
                                      <div className="col-lg-12">
                                        <AppTextArea
                                          title={ReactDOMServer.renderToString(
                                            <Translator
                                              file="module-intelligence"
                                              name="description"
                                            />,
                                          )}
                                          icon={
                                            <AppRemixIcon icon="chat-quote" />
                                          }
                                          onChange={(v) =>
                                            setFieldValue("comment", v)
                                          }
                                          value={values.comment}
                                          isClearable
                                        />
                                      </div>
                                    </div>
                                    {values.comment ? (
                                      <span
                                        className="btn btn-info btn-block"
                                        onClick={handleSubmit}
                                      >
                                        <Translator
                                          file="module-intelligence"
                                          name="generate.profile.reports"
                                        />
                                      </span>
                                    ) : (
                                      <span className="btn btn-info btn-block disabled">
                                        <Translator
                                          file="module-intelligence"
                                          name="generate.profile.reports"
                                        />
                                      </span>
                                    )}
                                  </>
                                )}
                            </>
                          )}
                        </Formik>
                      </div>
                    </div>
                  </div>
                </div>
                <AppLink
                  className="btn btn-outline-light btn-block mt-2"
                  path="user_module_intelligence_storedReports"
                  params={{ moduleBridgeHex }}
                >
                  <AppRemixIcon icon="inbox" size={16} />
                  <Translator file="module-intelligence" name="reports.store" />
                </AppLink>
              </div>
            </div>
            {onlineInfo && <OnlineInfoSwitch data={onlineInfo} />}
          </section>
          <AppModal
            show={showSaveProfileModal}
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-intelligence"
                name="module.int.save-profile"
              />,
            )}
            onClose={() => setShowSaveProfileModal(false)}
          >
            <Formik
              initialValues={{
                mode: "create",
                newProfileName: "",
              }}
              onSubmit={handleSaveProfile}
            >
              {({ handleSubmit }) => (
                <>
                  <div className="row">
                    <div className="col-sm-2 col-md-12 col-lg-3 col-xl-2">
                      <label className="col-form-label">
                        <Translator file="module-intelligence" name="name" />
                      </label>
                    </div>
                    <div className="col-sm-10 col-md-12 col-lg-9 col-xl-10">
                      <Field
                        name="newProfileName"
                        className="form-control mb-2"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <span
                        className="btn btn-info btn-block"
                        onClick={handleSubmit}
                      >
                        <Translator file="module-intelligence" name="save" />
                      </span>
                    </div>
                  </div>
                </>
              )}
            </Formik>
          </AppModal>
          <AppModal
            show={showLoadProfileModal}
            title={ReactDOMServer.renderToString(
              <Translator file="module-intelligence" name="profiles" />,
            )}
            onClose={() => setShowLoadProfileModal(false)}
          >
            <Formik
              initialValues={{
                profile: "",
              }}
              onSubmit={handleLoadProfile}
            >
              {({ setFieldValue, handleSubmit, values }) => (
                <>
                  <div className="row">
                    {intelligenceProfiles && intelligenceProfiles.length > 0 ? (
                      <div className="col-lg-12">
                        <AppMultiSelect
                          options={intelligenceProfiles}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-intelligence"
                              name="name"
                            />,
                          )}
                          icon={<AppRemixIcon icon="draft" />}
                          onChange={(e) => setFieldValue("profile", e)}
                          placeholder={ReactDOMServer.renderToString(
                            <Translator
                              file="module-intelligence"
                              name="select.profile"
                            />,
                          )}
                          value={values.profile}
                          setLabelField="name"
                          setValueField="id"
                        />
                      </div>
                    ) : (
                      <div className="col-sm-10 col-md-12 col-lg-9 col-xl-10">
                        <div className="form-control mb-3">
                          <Translator
                            file="module-intelligence"
                            name="no.profiles"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-12 mt-4">
                      <small
                        className="text-danger float-right pointer"
                        onClick={() => {
                          setFieldValue("mode", "delete");
                          handleSubmit();
                        }}
                      >
                        <AppRemixIcon icon="delete-bin" />{" "}
                        <Translator
                          file="module-intelligence"
                          name="delete.selected.profile"
                        />
                      </small>
                    </div>
                  </div>
                </>
              )}
            </Formik>
          </AppModal>
        </>
      ) : (
        <div style={{ marginTop: "200px" }}>
          <NoInfo icon={<SpinnerLoader />} text="" color={colors.primary} />
        </div>
      )}
    </div>
  );
};

export default IntelligenceHomeScreen;
